import price from "./calculatePrice";

/**
 * Calculates GST charges with discount and other charges.
 * @param {number} amount - The base amount.
 * @param {number} gstRate - GST rate in percent.
 * @param {boolean} isInclusive - If true, GST is inclusive; else exclusive.
 * @param {number} discount - Discount to subtract.
 * @param {number} otherCharges - Charges to add.
 * @returns {object} - { gstAmount, totalAmount }
 */
function calculateGST(
  amount,
  gstRate,
  isExclusive = true,
  discount = 0,
  otherCharges = 0,
) {
  const adjustedAmount = amount;
  let gstAmount, totalAmount;
  if (isExclusive) {
    gstAmount = adjustedAmount * (gstRate / 100);
    totalAmount = adjustedAmount + gstAmount;
  } else {
    gstAmount = adjustedAmount - adjustedAmount / (1 + gstRate / 100);
    totalAmount = adjustedAmount;
  }
  return {
    gstAmount: parseFloat(gstAmount),
    totalAmount: parseFloat(totalAmount),
  };
}

/**
 * Calculates order totals including taxes, discounts, and other charges.
 * @param {object} activeOrder - The active order object containing order items.
 * @param {number} cgst - CGST rate in percent.
 * @param {number} sgst - SGST rate in percent.
 * @param {number} otherCharges - Additional charges to add to the total.
 * @param {number} discount - Discount to subtract from the total.
 * @param {string} tableType - Type of table (for price calculation).
 * @returns {object} - Object containing detailed totals.
 */

const calculateOrderTotals = (
  activeOrder,
  cgst,
  sgst,
  otherCharges,
  discount,
  tableType,
  exclusiveGST,
) => {
  let itemsTotal = 0;
  let cgstCharges = 0;

  let sgstCharges = 0;
  let grandTotal = 0;
  let grandTotalWithoutDiscount = 0;

  let tablePrice = 0;
  let taxTotal = 0;
  let subTotal = 0;
  let gstItemsTotal = 0;

  if (activeOrder?.orderItems) {
    // Calculate total price for all items (gstItemsTotal)
    activeOrder?.orderItems.forEach((item) => {
      gstItemsTotal +=
        item.quantity * (price(tableType, item) || item.itemPrice);
    });

    // If a discount percentage is stored, dynamically calculate the flat discount amount
    if (activeOrder?.discountPercentage && activeOrder.discountPercentage > 0) {
      discount = (gstItemsTotal * activeOrder.discountPercentage) / 100;
    }

    // Distribute chargeDifference proportionally to each item
    activeOrder?.orderItems.forEach((item) => {
      const itemBaseTotal =
        item.quantity * (price(tableType, item) || item.itemPrice);
      // Proportional share of discount
      const itemDiscountShare =
        gstItemsTotal > 0 ? discount * (itemBaseTotal / gstItemsTotal) : 0;

      const itemTotalAfterDiscount = itemBaseTotal - itemDiscountShare;

      // Calculate GST for this item (exclusive GST)
      const { gstAmount, totalAmount } = calculateGST(
        itemTotalAfterDiscount,
        (item?.cgst || 0) + (item?.sgst || 0),
        exclusiveGST, // false = inclusive / true = exclusive GST
      );

      // itemsTotal calculation is now done outside the loop to prevent precision loss
      // subTotal += exclusiveGST ? totalAmount : (totalAmount - gstAmount); // or use totalAmount if you want GST included
      subTotal += itemBaseTotal;
      // Split GST equally between CGST and SGST
      cgstCharges += parseFloat(gstAmount / 2);
      sgstCharges += parseFloat(gstAmount / 2);
    });

    tablePrice = activeOrder.tablePrice;
  }

  taxTotal = parseFloat(cgstCharges + sgstCharges);

  // Calculate itemsTotal directly to avoid proportional floating point errors
  if (exclusiveGST) {
    itemsTotal = gstItemsTotal - discount + taxTotal;
  } else {
    itemsTotal = gstItemsTotal - discount;
  }

  grandTotal = itemsTotal + parseFloat(otherCharges || 0);
  grandTotalWithoutDiscount = gstItemsTotal;

  console.log("itemsTotal", itemsTotal);

  return {
    itemsTotal,
    subTotal,
    cgstCharges,
    sgstCharges,
    otherCharges,
    taxTotal,
    gstItemsTotal,
    discount,
    tablePrice,
    refId: activeOrder?.refId,
    grandTotal: grandTotal.toFixed(2),
    grandTotalWithoutDiscount: grandTotalWithoutDiscount.toFixed(2),
    cgst,
    sgst,
    isExclusiveGST: exclusiveGST,
  };
};

export default calculateOrderTotals;
